import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  FiArrowUpRight,
  FiEdit,
  FiLayers,
  FiPlus,
  FiTrash2,
} from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import SideBar from "../../components/sidebar/Sidebar";
import UserLoginData from "../Auth/UserLoginData";
import { useListHook } from "../Admin/List/hook";
import { useUpdateStatus } from "../Admin/Modal/ClientsAssign/hook";
import { listCampaignsApi, deleteCampaignApi } from "./api";
import CreateCampaignModal from "./CreateCampaignModal";
import "./campaigns.css";

export const CampaignListScreen = () => {
  const { isLoggedIn } = UserLoginData();
  const { clientList = [] } = useListHook();
  const { defaultData } = useUpdateStatus(isLoggedIn?.response?._id);
  const navigate = useNavigate();

  const role = isLoggedIn?.response?.role || "admin";
  const clientOptions = role === "admin" ? clientList : defaultData;
  const visibleClients = Array.isArray(clientOptions) ? clientOptions : [];

  const [searchParams, setSearchParams] = useSearchParams();
  const storageKey = `campaigns.clientId.${isLoggedIn?.response?._id || role}`;
  let savedClientId = "all";
  try {
    savedClientId = localStorage.getItem(storageKey) || "all";
  } catch {}
  const clientId = searchParams.get("clientId") || savedClientId;

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    const requestClientId =
      role === "client"
        ? isLoggedIn?.response?._id
        : role === "operational"
        ? clientId === "all"
          ? isLoggedIn?.response?.clients || []
          : [clientId]
        : clientId;

    const [data, error] = await listCampaignsApi(requestClientId, role);

    if (error) {
      const message = error.response?.data?.message || "Could not load campaigns.";
      setLoadError(message);
      toast.error(message);
      setCampaigns([]);
    } else {
      setCampaigns(data?.campaigns || []);
    }
    setLoading(false);
  }, [clientId, isLoggedIn, role]);

  useEffect(() => {
    if (role === "client") return;
    try {
      localStorage.setItem(storageKey, clientId);
    } catch {}
    if (!searchParams.get("clientId")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("clientId", clientId);
      setSearchParams(nextParams, { replace: true });
    }
  }, [clientId, role, searchParams, setSearchParams, storageKey]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleClientChange = (event) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("clientId", event.target.value);
    setSearchParams(nextParams, { replace: true });
  };

  const handleOpenCreate = () => {
    setEditingCampaign(null);
    setShowModal(true);
  };

  const handleOpenEdit = (campaign) => {
    setEditingCampaign(campaign);
    setShowModal(true);
  };

  const handleDelete = async (campaign) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${campaign.name}"? (Underlying short links will NOT be deleted).`
      )
    ) {
      return;
    }

    const [, error] = await deleteCampaignApi(campaign._id);
    if (error) {
      toast.error(error.response?.data?.message || "Failed to delete campaign");
    } else {
      toast.success("Campaign deleted");
      fetchCampaigns();
    }
  };

  return (
    <SideBar>
      <section className="campaigns-page" aria-labelledby="campaigns-title">
        <header className="campaigns-header">
          <div className="campaigns-header-title">
            <span className="campaigns-header-icon" aria-hidden="true">
              <FiLayers />
            </span>
            <div>
              <h1 id="campaigns-title">Campaigns</h1>
              <p>Club multiple short links together for unified, collective analytics.</p>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={handleOpenCreate}
          >
            <FiPlus aria-hidden="true" />
            <span>Create Campaign</span>
          </button>
        </header>

        {/* Toolbar */}
        {role !== "client" && (
          <div className="campaigns-toolbar">
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="campaigns-client-filter" className="small fw-bold">
                Client:
              </label>
              <select
                id="campaigns-client-filter"
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={clientId}
                onChange={handleClientChange}
              >
                <option value="all">All clients</option>
                {visibleClients.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.username}
                  </option>
                ))}
              </select>
            </div>

            <span className="text-muted small">
              {campaigns.length} campaign{campaigns.length === 1 ? "" : "s"}
            </span>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5 text-muted">Loading campaigns...</div>
        ) : loadError ? (
          <div role="alert" className="card p-4">
            <p>{loadError}</p>
            <button type="button" className="btn btn-primary" onClick={fetchCampaigns}>Retry</button>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="card text-center p-5 border-dashed">
            <div className="mb-3">
              <FiLayers size={36} className="text-muted" aria-hidden="true" />
            </div>
            <h3 style={{ fontSize: "18px", fontWeight: 700 }}>No campaigns yet</h3>
            <p className="text-muted small" style={{ maxWidth: "400px", margin: "0 auto 16px" }}>
              Club short links into a campaign to measure collective engagement over time, traffic
              attribution, and audience demographics.
            </p>
            <div>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleOpenCreate}>
                Create your first campaign
              </button>
            </div>
          </div>
        ) : (
          <div className="campaigns-table-wrap" role="region" aria-label="Campaigns table" tabIndex={0}>
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th scope="col">Campaign</th>
                  <th scope="col">Client</th>
                  <th scope="col" className="campaigns-table-number">Clubbed Links</th>
                  <th scope="col" className="campaigns-table-number">Collective Clicks</th>
                  <th scope="col">Created</th>
                  <th scope="col" className="campaigns-table-actions-heading">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c._id}>
                    <th scope="row" className="campaigns-table-name">
                      <button
                        type="button"
                        className="campaigns-name-link"
                        onClick={() => navigate(`/campaigns/${c._id}`)}
                      >
                        {c.name}
                      </button>
                      {c.description && (
                        <p className="campaigns-table-description" title={c.description}>
                          {c.description}
                        </p>
                      )}
                    </th>
                    <td>
                      {c.clientName ? <span className="campaign-card-badge">{c.clientName}</span> : "-"}
                    </td>
                    <td className="campaigns-table-number">{Number(c.total_links || 0).toLocaleString()}</td>
                    <td className="campaigns-table-number">{Number(c.total_clicks || 0).toLocaleString()}</td>
                    <td className="campaigns-table-date">
                    {c.createdAt
                      ? new Date(c.createdAt).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "-"}
                    </td>

                    <td>
                      <div className="campaigns-table-actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleOpenEdit(c)}
                      title="Edit campaign & manage links"
                      aria-label={`Edit ${c.name}`}
                    >
                      <FiEdit aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(c)}
                      title="Delete campaign"
                      aria-label={`Delete ${c.name}`}
                    >
                      <FiTrash2 aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                      onClick={() => navigate(`/campaigns/${c._id}`)}
                      title="View collective analytics"
                      aria-label={`View analytics for ${c.name}`}
                    >
                      <span>Analytics</span>
                      <FiArrowUpRight aria-hidden="true" />
                    </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <CreateCampaignModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onSuccess={fetchCampaigns}
          campaignToEdit={editingCampaign}
          availableClients={visibleClients}
          currentRole={role}
          currentClientId={clientId}
        />
      </section>
    </SideBar>
  );
};

export default CampaignListScreen;
